// Blog utility functions for SEO, formatting, and content management

export const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-ZA', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

export const formatReadTime = (readTime) => {
  return readTime || '5 min read';
};

export const generateExcerpt = (content, maxLength = 160) => {
  // Remove markdown syntax for excerpt
  const plainText = content
    .replace(/#{1,6}\s+/g, '') // Remove headers
    .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold
    .replace(/\*(.*?)\*/g, '$1') // Remove italic
    .replace(/`(.*?)`/g, '$1') // Remove inline code
    .replace(/\[(.*?)\]\(.*?\)/g, '$1') // Remove links, keep text
    .replace(/\n/g, ' ') // Replace newlines with spaces
    .trim();

  if (plainText.length <= maxLength) {
    return plainText;
  }

  return plainText.substring(0, maxLength).trim() + '...';
};

export const generateSEOMetadata = (post) => {
  return {
    title: `${post.title} | PDF Statement Converter`,
    description: post.metaDescription || generateExcerpt(post.content),
    keywords: post.tags ? post.tags.join(', ') : '',
    author: post.author || 'PDF Statement Converter Team',
    publishDate: post.publishDate,
    url: `https://pdfstatementconverter.co.za/blog/${post.slug}`,
    image: `https://pdfstatementconverter.co.za/api/og-image?title=${encodeURIComponent(post.title)}`,
    type: 'article'
  };
};

export const generateStructuredData = (post) => {
  const metadata = generateSEOMetadata(post);
  
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": post.title,
    "description": metadata.description,
    "author": {
      "@type": "Organization",
      "name": post.author || "PDF Statement Converter Team",
      "url": "https://pdfstatementconverter.co.za"
    },
    "publisher": {
      "@type": "Organization",
      "name": "PDF Statement Converter",
      "url": "https://pdfstatementconverter.co.za",
      "logo": {
        "@type": "ImageObject",
        "url": "https://pdfstatementconverter.co.za/logo512.png"
      }
    },
    "datePublished": post.publishDate,
    "dateModified": post.publishDate,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": metadata.url
    },
    "keywords": post.tags ? post.tags.join(', ') : '',
    "articleSection": "Accounting",
    "inLanguage": "en-ZA"
  };
};

export const processMarkdownContent = (content) => {
  // Simple markdown processing for basic formatting
  return content
    .replace(/^# (.*$)/gm, '<h1 class="text-3xl font-bold text-gray-900 mb-6">$1</h1>')
    .replace(/^## (.*$)/gm, '<h2 class="text-2xl font-bold text-gray-900 mb-4 mt-8">$1</h2>')
    .replace(/^### (.*$)/gm, '<h3 class="text-xl font-semibold text-gray-900 mb-3 mt-6">$1</h3>')
    .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold">$1</strong>')
    .replace(/\*(.*?)\*/g, '<em class="italic">$1</em>')
    .replace(/`(.*?)`/g, '<code class="bg-gray-100 px-2 py-1 rounded text-sm font-mono">$1</code>')
    .replace(/```([\s\S]*?)```/g, '<pre class="bg-gray-100 p-4 rounded-lg overflow-x-auto mb-4"><code class="text-sm">$1</code></pre>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-indigo-600 hover:text-indigo-800 underline">$1</a>')
    .replace(/^\- (.*$)/gm, '<li class="mb-2">$1</li>')
    .replace(/^(\d+)\. (.*$)/gm, '<li class="mb-2">$2</li>')
    .replace(/\n\n/g, '</p><p class="text-gray-700 mb-4">')
    .replace(/^(?!<[h|l|p|d|c|s])/gm, '<p class="text-gray-700 mb-4">')
    .replace(/$(?![>])/gm, '</p>');
};

export const getBlogBreadcrumbs = (post = null) => {
  const breadcrumbs = [
    { name: 'Home', href: '/' },
    { name: 'Blog', href: '/blog' }
  ];

  if (post) {
    breadcrumbs.push({ name: post.title, href: `/blog/${post.slug}` });
  }

  return breadcrumbs;
};

export const getRelatedPosts = (currentPost, allPosts, limit = 3) => {
  if (!currentPost || !allPosts) return [];

  // Find posts with matching tags
  const relatedPosts = allPosts
    .filter(post => post.id !== currentPost.id)
    .map(post => {
      const matchingTags = post.tags ? 
        post.tags.filter(tag => currentPost.tags?.includes(tag)).length : 0;
      return { ...post, relevanceScore: matchingTags };
    })
    .sort((a, b) => b.relevanceScore - a.relevanceScore)
    .slice(0, limit);

  // If we don't have enough related posts, fill with recent posts
  if (relatedPosts.length < limit) {
    const additionalPosts = allPosts
      .filter(post => 
        post.id !== currentPost.id && 
        !relatedPosts.find(related => related.id === post.id)
      )
      .slice(0, limit - relatedPosts.length);
    
    relatedPosts.push(...additionalPosts);
  }

  return relatedPosts.slice(0, limit);
};

export const generateBlogSitemap = (posts) => {
  const baseUrl = 'https://pdfstatementconverter.co.za';
  
  const sitemapEntries = posts.map(post => ({
    url: `${baseUrl}/blog/${post.slug}`,
    lastmod: post.publishDate,
    changefreq: 'monthly',
    priority: post.featured ? '0.8' : '0.6'
  }));

  // Add blog index page
  sitemapEntries.unshift({
    url: `${baseUrl}/blog`,
    lastmod: new Date().toISOString().split('T')[0],
    changefreq: 'weekly',
    priority: '0.7'
  });

  return sitemapEntries;
};

export const validatePost = (post) => {
  const errors = [];
  
  if (!post.title) errors.push('Title is required');
  if (!post.slug) errors.push('Slug is required');
  if (!post.content) errors.push('Content is required');
  if (!post.publishDate) errors.push('Publish date is required');
  if (!post.excerpt && !post.content) errors.push('Excerpt or content is required for meta description');
  
  // Validate slug format
  if (post.slug && !/^[a-z0-9-]+$/.test(post.slug)) {
    errors.push('Slug must contain only lowercase letters, numbers, and hyphens');
  }
  
  // Validate date format
  if (post.publishDate && isNaN(Date.parse(post.publishDate))) {
    errors.push('Invalid publish date format');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};